import {
    ContentType,
    GeneralDetailError,
    ICorporationResponse,
    ILocationResponse,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { useRootFolder } from 'custom_hooks/data_hooks/file/useRootFolder';
import {
    defaultMaxFileNameLength,
    defaultMaxFileSize,
    FileNameTooLongError,
    FileTooLargeError,
    FileUpload,
    isValidPicture,
    UnsupportedFileTypeError,
} from 'pages/Settings/FileLibrary/shared';

export type UseFileUpload = {
    /**
     * The corporation to upload the file to.
     */
    corporation?: ICorporationResponse;
    /**
     * The location to upload the file to.
     */
    location?: ILocationResponse;
    /**
     * The group Ids to associate with the file
     */
    groupIds?: string[];
    /**
     * The maximum file size (in bytes) that can be uploaded.
     *
     * NOTE: This is just client-side validation, the file size must still be
     * validated on the server.
     */
    maxFileSize?: number;
    /**
     * The maximum file name length.
     *
     * NOTE: This is just client-side validation, the file name length must still
     * be validated on the server.
     */
    maxFileNameLength?: number;
    /**
     * Parent Folder's Id. If unsupplied, will use the Root folder's id
     */
    parentFolderId?: string;
};
type UseFileUploadOptions = UseMutationOptions<
    IPictureFileMetadataResponse,
    GeneralDetailError | Error,
    FileUpload
>;
export function useFileUpload(
    {
        location,
        corporation,
        groupIds,
        maxFileSize = defaultMaxFileSize,
        maxFileNameLength = defaultMaxFileNameLength,
        parentFolderId,
    }: UseFileUpload,
    options?: UseFileUploadOptions
) {
    const { data: rootFolder } = useRootFolder({
        corporationId: corporation?.Id,
        locationId: location?.Id,
    });

    return useMutation({
        mutationFn: (file: FileUpload) => {
            if (!isValidPicture(file.data)) {
                throw new UnsupportedFileTypeError();
            }
            if (file.data.size > maxFileSize) {
                throw new FileTooLargeError();
            }
            if (file.name.length > maxFileNameLength) {
                throw new FileNameTooLongError();
            }
            return sdClient.createFile({
                fileType: ContentType.Picture,
                displayName: file.name,
                parentFolderId: parentFolderId ?? rootFolder?.Id,
                locationId: location?.Id,
                corporationId: corporation?.Id,
                groupIds,
                files: {
                    data: file.data,
                    fileName: file.name,
                },
            });
        },

        ...options,
    });
}
